import { FormLabel, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRole, ProjectUser } from "@nens/rana/api-v1";
import { RanaModal } from "@nens/ranalib";
import { RanaErrors } from "@nens/ranalib";

import { cancelButtonText, userRoleDescriptionString, userRoleString } from "@/translations";

import { useUpdateProjectUsers } from "../api/update-project-user";

interface ProjectUserEditableRoleModalProps {
  users: ProjectUser[];
  onClose: () => void;
}

export function ProjectUserEditableRoleModal({
  users,
  onClose,
}: ProjectUserEditableRoleModalProps) {
  const intl = useIntl();
  const currentRole = users[0].role;
  const [role, setRole] = useState<ProjectUser["role"]>(currentRole);
  const { mutate, errors, isMutating } = useUpdateProjectUsers(users, onClose);
  const saveRole = () => mutate(role);

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={<FormattedMessage id="title.edit_project_role" defaultMessage="Edit project role" />}
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: intl.formatMessage({
            id: "button.save",
            defaultMessage: "Save",
          }),
          variant: "primary",
          isDisabled: isMutating || (users.length === 1 && role === currentRole),
          onClick: saveRole,
        },
      ]}
    >
      <RadioGroup onChange={(newRole: ProjectUser["role"]) => setRole(newRole)} value={role}>
        <Stack>
          {Object.values(ProjectRole).map((role) => (
            <Radio key={role} value={role}>
              <FormLabel as="strong">{userRoleString(intl, role)}</FormLabel>
              <Text>{userRoleDescriptionString(intl, role)}</Text>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      <RanaErrors errors={errors} />
    </RanaModal>
  );
}
